import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { X, Phone, Envelope, House, Wrench, CurrencyDollar, Warning } from "@phosphor-icons/react";

const $ = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/**
 * Landlord Quick Card — anywhere in the app pass `landlordId` + button label.
 * Renders a click-to-open mini modal with the essentials: contact, active
 * properties, open WOs, last completed WO, 30-day vendor spend.
 */
export default function LandlordQuickCard({ landlordId, label = "Quick view", className = "" }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || !landlordId || data) return;
    api.get(`/landlord-quick-card/${landlordId}`)
      .then(r => setData(r.data))
      .catch(() => setData({ error: true }));
  }, [open, landlordId, data]);

  if (!landlordId) return null;

  return (
    <>
      <button onClick={e => { e.preventDefault(); e.stopPropagation(); setOpen(true); }}
        data-testid={`landlord-quick-${landlordId}`}
        className={className || "inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest text-cyan-700 hover:text-cyan-900"}>
        @{label}
      </button>
      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4"
          data-testid="landlord-quick-modal" onClick={() => setOpen(false)}>
          <div className="bg-white border-4 border-[#0f172a] max-w-md w-full my-8 shadow-[8px_8px_0_#000]"
            onClick={e => e.stopPropagation()}>
            {!data && <div className="p-8 text-center text-slate-400 text-sm animate-pulse">Loading…</div>}
            {data?.error && <div className="p-6 text-center text-red-600">Couldn't load — landlord may have been deleted.</div>}
            {data && !data.error && (
              <>
                <div className="p-4 border-b-2 border-slate-900 flex items-center justify-between"
                  style={{ backgroundColor: data.brand_color || "#0891b2", color: "#fff" }}>
                  <div className="flex items-center gap-3 min-w-0">
                    {data.brand_logo && <img src={data.brand_logo} alt="" className="w-12 h-12 object-cover border-2 border-white flex-shrink-0"/>}
                    <div className="min-w-0">
                      <div className="text-[10px] font-black uppercase tracking-widest opacity-90">Landlord</div>
                      <div className="font-display font-black text-xl truncate">{data.name}</div>
                      {data.principal_name && <div className="text-[11px] opacity-80 truncate">c/o {data.principal_name}</div>}
                    </div>
                  </div>
                  <button onClick={() => setOpen(false)}
                    className="w-10 h-10 border-2 border-white/50 bg-white/10 hover:bg-white/20 flex items-center justify-center flex-shrink-0">
                    <X weight="bold" size={16}/>
                  </button>
                </div>

                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <a href={`tel:${data.phone}`} className={`p-2 border-2 border-slate-900 hover:bg-cyan-50 ${!data.phone && "opacity-40 pointer-events-none"}`}>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1"><Phone size={10}/> Phone</div>
                      <div className="font-mono font-black text-sm">{data.phone || "—"}</div>
                    </a>
                    <a href={`mailto:${data.email}`} className={`p-2 border-2 border-slate-900 hover:bg-cyan-50 ${!data.email && "opacity-40 pointer-events-none"}`}>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1"><Envelope size={10}/> Email</div>
                      <div className="font-black text-sm truncate">{data.email || "—"}</div>
                    </a>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <Stat icon={<House weight="fill" size={12}/>} label="Props" value={data.property_count}/>
                    <Stat icon={<Wrench weight="fill" size={12}/>} label="Open" value={data.open_wo_count} accent={data.urgent_wo_count > 0 ? "text-red-700" : ""} sub={data.urgent_wo_count > 0 ? `${data.urgent_wo_count} urgent` : ""}/>
                    <Stat icon={<CurrencyDollar weight="fill" size={12}/>} label="30d Spend" value={$(data.vendor_spend_30d_cents)}/>
                  </div>

                  {data.properties && data.properties.length > 0 && (
                    <div>
                      <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 mb-1">Active properties</div>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {data.properties.map(p => (
                          <Link key={p} to={`/admin/properties/${encodeURIComponent(p)}`}
                            onClick={() => setOpen(false)}
                            className="block p-1.5 text-xs bg-slate-50 hover:bg-cyan-50 border border-slate-200 truncate">
                            {p}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {data.last_completed_wo && (
                    <div className="p-2 bg-emerald-50 border border-emerald-300 text-xs">
                      <div className="text-[9px] font-black uppercase tracking-widest text-emerald-700">Last completed WO</div>
                      <div className="font-bold truncate">{data.last_completed_wo.title}</div>
                      <div className="text-[10px] text-slate-500">{(data.last_completed_wo.completed_at || "").slice(0, 10)}</div>
                    </div>
                  )}

                  {data.notes && (
                    <div className="p-2 bg-amber-50 border border-amber-300 text-xs italic text-amber-800">
                      <div className="text-[9px] font-black uppercase tracking-widest text-amber-700 mb-0.5 not-italic">Notes</div>
                      {data.notes}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

const Stat = ({ icon, label, value, accent, sub }) => (
  <div className="p-2 border-2 border-slate-900">
    <div className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex items-center gap-1">{icon}{label}</div>
    <div className={`font-display font-black text-lg tabular-nums ${accent || ""}`}>{value}</div>
    {sub && <div className={`text-[9px] font-black uppercase tracking-widest ${accent || "text-slate-500"}`}>{sub}</div>}
  </div>
);
